import { useEffect, useRef } from "react";
import { Volume2, VolumeX, Loader2 } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTextToSpeech } from "@/hooks/use-text-to-speech";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface SpeakLabels {
  idle: string;
  speaking: string;
}

interface SpeakButtonProps {
  /** Text to synthesize. */
  text: string;

  // TTS options forwarded to the hook
  rate?: number;
  lang?: string;
  pitch?: number;
  volume?: number;

  // Behavior
  /** Stop the click from bubbling to a parent card/row. */
  stopPropagation?: boolean;
  /** What to do when speech synthesis is unsupported. Defaults to hiding the button. */
  unsupportedBehavior?: "hide" | "disable";
  /** Auto-speak on mount and whenever this key changes (e.g. flashcard flip). */
  autoSpeakKey?: string | number;

  // Presentation
  /** Apply button-variant chrome (outline/secondary/etc). Omit for a bare icon button. */
  variant?: "ghost" | "outline" | "default" | "secondary";
  /** Button size when `variant` is set. */
  size?: "sm" | "icon" | "default" | "lg";
  /** Visible text label that reflects state (e.g. "Listen" / "Stop"). */
  label?: SpeakLabels;
  /** Wrap the button in a tooltip whose text reflects state. */
  tooltip?: SpeakLabels;
  tooltipSide?: "top" | "bottom" | "left" | "right";
  /** Class on the button element (sizing, hover, padding). */
  className?: string;
  /** Class on the icon (size + base color). */
  iconClassName?: string;
  /** Accessible label override. */
  ariaLabel?: string;
}

/**
 * Reusable text-to-speech trigger. Owns its own `useTextToSpeech` instance and
 * surfaces the four states the UI cares about: idle (Volume2), loading
 * (spinner), speaking (VolumeX → stop), and error (idle icon + tooltip/toast).
 */
export const SpeakButton = ({
  text,
  rate,
  lang = "en-US",
  pitch,
  volume,
  stopPropagation = false,
  unsupportedBehavior = "hide",
  autoSpeakKey,
  variant,
  size = "sm",
  label,
  tooltip,
  tooltipSide = "bottom",
  className,
  iconClassName,
  ariaLabel,
}: SpeakButtonProps) => {
  const { t } = useTranslation();
  const { speak, stop, isSpeaking, isLoading, isSupported, browserSupported, voices, error, checkSupport } =
    useTextToSpeech({ rate, lang, pitch, volume });
  const { toast } = useToast();
  const autoSpeakTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Auto-speak when the keyed content first appears or changes.
  // Retries once voices become available if they weren't loaded yet.
  useEffect(() => {
    if (autoSpeakKey === undefined || !isSupported) return;
    if (voices.length === 0) return;
    if (autoSpeakTimerRef.current) clearTimeout(autoSpeakTimerRef.current);
    autoSpeakTimerRef.current = setTimeout(() => speak(text), 300);
    return () => {
      if (autoSpeakTimerRef.current) clearTimeout(autoSpeakTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSpeakKey, voices.length]);

  if (!isSupported && unsupportedBehavior === "hide") {
    return null;
  }

  const active = isLoading || isSpeaking;

  const handleClick = (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();

    if (!browserSupported) {
      checkSupport();
      if (!browserSupported) {
        toast({
          title: "Text-to-Speech Not Available",
          description: error || "Please use a modern browser like Chrome, Firefox, Safari, or Edge.",
          variant: "destructive",
        });
        return;
      }
    }

    if (active) {
      stop();
    } else {
      speak(text);
    }
  };

  const idleText = label?.idle ?? tooltip?.idle ?? t("pages.speak.idle", "Listen");
  const speakingText = label?.speaking ?? tooltip?.speaking ?? t("pages.speak.speaking", "Stop");
  const stateLabel = isSpeaking ? speakingText : idleText;

  const icon = isLoading ? (
    <Loader2 className={cn("animate-spin text-primary", iconClassName)} />
  ) : isSpeaking ? (
    <VolumeX className={cn("text-primary", iconClassName)} />
  ) : (
    <Volume2
      className={cn(
        error ? "text-primary/70" : "text-muted-foreground hover:text-primary",
        iconClassName,
      )}
    />
  );

  const button = (
    <button
      type="button"
      onClick={handleClick}
      aria-label={ariaLabel ?? stateLabel}
      title={error || stateLabel}
      className={cn(
        variant ? buttonVariants({ variant, size }) : "inline-flex items-center justify-center transition-colors",
        active && "text-primary",
        className,
      )}
    >
      {icon}
      {label && <span>{stateLabel}</span>}
    </button>
  );

  if (!tooltip) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side={tooltipSide}>
        <p>{error || stateLabel}</p>
      </TooltipContent>
    </Tooltip>
  );
};
